import { supabase } from '@/integrations/supabase/client';

export interface ExternalSubscriptionService {
  fetchAllSubscriptions: (params: {
    connectedAccountId: string;
    limit?: number;
    statusFilter?: string;
  }) => Promise<{
    subscriptions: any[];
    pagination: any;
    statistics: any;
  }>;
  calculateStatusStatistics: (subscriptions: any[]) => Record<string, number>;
}

export const externalSubscriptionService: ExternalSubscriptionService = {
  async fetchAllSubscriptions({ connectedAccountId, limit = 100, statusFilter = 'all' }) {
    try {
      console.log('[ExternalSubscriptionService] Fetching ALL subscriptions from edge function...');
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('User session not found. Please log in.');
      }

      let allSubscriptions: any[] = [];
      let hasMore = true;
      let startingAfter: string | null = null;
      let totalRequests = 0;
      const maxRequests = 50; // Safety limit to prevent infinite loops
      
      // Fetch all subscriptions with automatic pagination
      while (hasMore && totalRequests < maxRequests) {
        totalRequests++;
        console.log(`[ExternalSubscriptionService] Fetching batch ${totalRequests}, starting after: ${startingAfter}`);
        
        const requestBody: any = {
          account: connectedAccountId,
          limit,
          status_filter: statusFilter
        };
        
        // Add pagination cursor if we have one
        if (startingAfter) {
          requestBody.starting_after = startingAfter;
        }

        const { data, error } = await supabase.functions.invoke('list-non-km-subscriptions', {
          body: requestBody
        });

        if (error) {
          console.error('[ExternalSubscriptionService] Edge function error:', error);
          throw new Error(`Edge function error: ${error.message}`);
        }

        if (!data) {
          console.warn('[ExternalSubscriptionService] No data received from edge function');
          break;
        }

        const batchSubscriptions = data.subscriptions || [];
        allSubscriptions = allSubscriptions.concat(batchSubscriptions);
        
        // Check if there are more pages - using the correct structure from edge function
        hasMore = data.has_more === true;
        startingAfter = data.pagination?.next_cursor || null;
        
        console.log(`[ExternalSubscriptionService] Batch ${totalRequests} completed:`, {
          batchSize: batchSubscriptions.length,
          totalSoFar: allSubscriptions.length,
          hasMore,
          nextCursor: startingAfter
        });
        
        // If we got less than the limit, we've probably reached the end
        if (batchSubscriptions.length < limit) {
          hasMore = false;
        }
        
        // Add a small delay between requests to be respectful to Stripe API and avoid rate limits
        if (hasMore) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      if (totalRequests >= maxRequests) {
        console.warn(`[ExternalSubscriptionService] Reached maximum request limit (${maxRequests}), there might be more subscriptions.`);
      }

      console.log('[ExternalSubscriptionService] Successfully fetched ALL subscriptions:', {
        total: allSubscriptions.length,
        totalRequests,
        completed: !hasMore,
        averagePerRequest: Math.round(allSubscriptions.length / totalRequests),
        timeEstimate: `${totalRequests * 0.2}s`
      });
      
      // Log a success message for large datasets
      if (allSubscriptions.length > 100) {
        console.log(`🎉 [ExternalSubscriptionService] Large dataset handled successfully! Fetched ${allSubscriptions.length} subscriptions across ${totalRequests} API calls.`);
      }

      return {
        subscriptions: allSubscriptions,
        pagination: { 
          hasMore: false, // We've fetched everything
          nextCursor: null,
          totalFetched: allSubscriptions.length,
          requestsMade: totalRequests
        },
        statistics: { 
          total: allSubscriptions.length, 
          byStatus: this.calculateStatusStatistics(allSubscriptions)
        }
      };

    } catch (error) {
      console.error('[ExternalSubscriptionService] Error fetching subscriptions:', error);
      throw error;
    }
  },

  // Helper method to calculate status statistics
  calculateStatusStatistics(subscriptions: any[]) {
    const stats: Record<string, number> = {};
    subscriptions.forEach(sub => {
      const status = sub.status || 'unknown';
      stats[status] = (stats[status] || 0) + 1;
    });
    return stats;
  }
};
